import { themeAtom } from "../../../shared/atoms";
import { ExcelLikeGrid } from "../../../shared/excel-grid/components/ExcelLikeGrid";
import { ExcelGridCellCommit, ExcelGridRow } from "../../../shared/excel-grid/models/excel-grid.models";
import { ADMIN_BQ_TQ_FORM_COLUMNS, AdminBqTqFormGridRow } from "../../models/admin-bq-tq-forms.models";
import {
  getAdminBqTqForms,
  getAdminBqTqFormsErrorMessage,
  toAdminBqTqFormGridRow,
} from "../../services/admin-bq-tq-forms.service";
import AdminBqTqFormsStatus, { AdminBqTqFormsLoadingState } from "./AdminBqTqFormsStatus";
import AdminBqTqFormsTopBar from "./AdminBqTqFormsTopBar";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const noopCellCommit = (_commit: ExcelGridCellCommit) => undefined;

/**
 * Admin navigator for Vessel Performance-owned BQ/TQ edits.
 */
const AdminBqTqFormsPanel = () => {
  const theme = useAtomValue(themeAtom);
  const navigate = useNavigate();
  const [rows, setRows] = useState<AdminBqTqFormGridRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<AdminBqTqFormGridRow | null>(null);
  const [loadingState, setLoadingState] = useState<AdminBqTqFormsLoadingState>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadForms = useCallback(async () => {
    setLoadingState("loading");
    setErrorMessage(null);

    try {
      const adminForms = await getAdminBqTqForms();
      setRows(adminForms.rows.map(toAdminBqTqFormGridRow));
      setLoadingState("ready");
    } catch (error) {
      setErrorMessage(getAdminBqTqFormsErrorMessage(error));
      setLoadingState("error");
    }
  }, []);

  useEffect(() => {
    void loadForms();
  }, [loadForms]);

  const handleSelectedRowChange = useCallback((row: ExcelGridRow | null) => {
    setSelectedRow(row as AdminBqTqFormGridRow | null);
  }, []);

  const handleOpenSelected = useCallback(() => {
    if (!selectedRow?.imoNumber || !selectedRow.vesselName) {
      return;
    }

    const vesselName = encodeURIComponent(selectedRow.vesselName);
    const imoNumber = encodeURIComponent(selectedRow.imoNumber);
    const path =
      selectedRow.formType === "Baltic Questionnaire"
        ? `/admin/bq-tq-admin/baltic-questionnaire/${imoNumber}/${vesselName}/general-info`
        : `/admin/bq-tq-admin/technical-form/${imoNumber}/${vesselName}/vessel-detail`;

    navigate(path);
  }, [navigate, selectedRow]);

  const formCountLabel = useMemo(() => `${rows.length} form row${rows.length === 1 ? "" : "s"}`, [rows.length]);

  return (
    <main
      data-test="admin-bq-tq-forms-page"
      className={twMerge("flex min-h-0 flex-1 overflow-hidden", theme.home.tailwind.textDefault)}
    >
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden" data-test="admin-bq-tq-forms-admin">
        <AdminBqTqFormsTopBar
          formCountLabel={formCountLabel}
          selectedRow={selectedRow}
          isRefreshing={loadingState === "loading"}
          onOpenSelected={handleOpenSelected}
          onRefresh={loadForms}
        />
        <AdminBqTqFormsStatus errorMessage={errorMessage} loadingState={loadingState} />
        <ExcelLikeGrid
          rows={rows}
          columns={ADMIN_BQ_TQ_FORM_COLUMNS}
          dataTest="admin-bq-tq-forms-grid"
          emptyLabel={loadingState === "loading" ? "Loading forms..." : "No Tech Form or Baltic Questionnaire rows."}
          onCellCommit={noopCellCommit}
          onSelectedRowChange={handleSelectedRowChange}
        />
      </div>
    </main>
  );
};

export default AdminBqTqFormsPanel;
